import React, { useEffect, useState } from 'react'
import { Badge, Button, Col, Modal, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { translateBackendError } from '../../utils/errorTranslator'
import { GroupCreateData, groupService } from '../../services/group.service'
import { PermissionAction, permissionsService, PermissionType } from '../../services/permissions.service'
import { toastService } from '../../services/toast.service'
import { Group } from '../../types'
import { InputText } from '../common/InputText'
import PermissionsSelect from '../common/PermissionsSelect'

interface GroupEditModalProps {
  show: boolean
  onHide: () => void
  group?: Group | null
  onSuccess: (group: Group) => void
  onError: (error: any) => void
}

interface GroupFormData {
  name: string
  permissions: string[]
}

// Minimal sidebar/tab visibility for new groups: Dashboard (always shown),
// Documents, Saved views, plus the standard UI-settings CRUD.
const defaultGroupPermissions = [
  permissionsService.getPermissionCode(PermissionAction.Add, PermissionType.UISettings),
  permissionsService.getPermissionCode(PermissionAction.View, PermissionType.UISettings),
  permissionsService.getPermissionCode(PermissionAction.Change, PermissionType.UISettings),
  permissionsService.getPermissionCode(PermissionAction.Delete, PermissionType.UISettings),
  permissionsService.getPermissionCode(PermissionAction.Display, PermissionType.Document),
  permissionsService.getPermissionCode(PermissionAction.Display, PermissionType.SavedView),
  permissionsService.getPermissionCode(PermissionAction.Display, PermissionType.UISettings),
]

const GroupEditModal: React.FC<GroupEditModalProps> = ({ show, onHide, group, onSuccess, onError }) => {
  const { t } = useTranslation()
  const [formData, setFormData] = useState<GroupFormData>({
    name: '',
    permissions: defaultGroupPermissions,
  })
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const isEdit = !!group
  const canChangeGroup = permissionsService.currentUserCan(PermissionAction.Change, PermissionType.Group)

  useEffect(() => {
    if (show) {
      if (group) {
        setFormData({
          name: group.name || '',
          permissions: group.permissions || [],
        })
      } else {
        setFormData({
          name: '',
          permissions: defaultGroupPermissions,
        })
      }
      setErrors({})
    }
  }, [show, group])

  const handleInputChange = (field: keyof GroupFormData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))

    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }))
    }
  }

  const handleSave = async () => {
    setLoading(true)
    setErrors({})

    try {
      const groupData: GroupCreateData = {
        name: formData.name,
        permissions: formData.permissions,
      }

      let savedGroup: Group
      if (isEdit && group) {
        savedGroup = await groupService.update(group.id, groupData)
      } else {
        savedGroup = await groupService.create(groupData)
      }

      toastService.showSuccess(t('saved_group', { name: savedGroup.name }))
      onSuccess(savedGroup)
      onHide()
    } catch (error: any) {
      const data = error.response?.data
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        const next: Record<string, string> = {}
        for (const [key, value] of Object.entries(data)) {
          if (Array.isArray(value)) {
            const parts = value
              .map((item) => (typeof item === 'string' ? translateBackendError(item, t) : String(item)))
              .filter(Boolean)
            if (parts.length) next[key] = parts.join(', ')
          } else if (typeof value === 'string') {
            next[key] = translateBackendError(value, t)
          }
        }
        if (Object.keys(next).length > 0) {
          setErrors(next)
        } else {
          onError(error)
        }
      } else {
        onError(error)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static">
      <form
        onSubmit={(e) => {
          e.preventDefault()
          handleSave()
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {isEdit ? t('edit_user_group') : t('create_new_user_group')}
            {group?.id && (
              <Badge bg="primary" className="ms-2">
                ID: {group.id}
              </Badge>
            )}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Row>
            <Col>
              <InputText
                title={t('name')}
                value={formData.name}
                onChange={(value) => handleInputChange('name', value)}
                error={errors.name}
                autocomplete="off"
                disabled={isEdit && !canChangeGroup}
              />

              <PermissionsSelect
                title={t('permissions')}
                value={formData.permissions}
                onChange={(permissions: string[]) => handleInputChange('permissions', permissions)}
                error={errors.permissions}
                disabled={isEdit && !canChangeGroup}
              />
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={loading}>
            {t('cancel')}
          </Button>
          <Button variant="primary" type="submit" disabled={loading || (isEdit && !canChangeGroup)}>
            {loading && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>}
            {t('save')}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  )
}

export default GroupEditModal
